import {
    ArrowLeftOutlined,
    DownloadOutlined,
    EditOutlined,
    SaveOutlined,
    SearchOutlined,
    SendOutlined
} from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, Input, Modal, Row, Select, Space, Statistic, Table, Tag } from "antd";
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import ModalTextForm from "../../components/modal/ModalTextForm";
import { trangThaiThue } from '../../constants';
import { formatVND } from '../../helpers/common';
import { Links } from "../../helpers/Links";
import {
    ChiTietThueThangItem,
    DanhSachThueThangResponse,
    DuLieuChiTietThueTongHopResponse,
    KyTinhThueKhongThuongXuyenResponse,
    ThongTinThueTongHopResponse
} from '../../models/thue/Thue';
import {
    capNhatThamChieuThueTongHop,
    duyetThueTongHop,
    layDanhSachKyThueKhongThuongXuyen,
    layDsThueThangTheoNam,
    layThongTinThueTongHop,
    luuThueTongHop,
    taiThueTongHop,
    xuatExcelThueTongHop
} from '../../services/thue';
import { DynamicColumnHeader } from '../../models/luong/Luong';

const ChiTietTongHopThue = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [idThueThang, setIdThueThang] = useState<number>(0);
    const [tuKhoa, setTuKhoa] = useState<string>('');
    const [tuKhoaFilter, setTuKhoaFilter] = useState<string>('');

    const [thongTin, setThongTin] = useState<ThongTinThueTongHopResponse | null>(null);
    const [dataChiTiet, setDataChiTiet] = useState<DuLieuChiTietThueTongHopResponse | null>(null);

    // Modal cấu hình tham chiếu
    const [openModalCauHinh, setOpenModalCauHinh] = useState<boolean>(false);
    const [formCauHinh] = Form.useForm();
    const [dsKyThueTNCNModal, setDsKyThueTNCNModal] = useState<Array<DanhSachThueThangResponse>>([]);
    const [dsKyThueKTXModal, setDsKyThueKTXModal] = useState<Array<KyTinhThueKhongThuongXuyenResponse>>([]);
    const [namLocTNCN, setNamLocTNCN] = useState<Dayjs | null>(dayjs());
    const [namLocKTX, setNamLocKTX] = useState<Dayjs | null>(dayjs());

    // Modal xác nhận duyệt
    const [isShowModalDuyet, setIsShowModalDuyet] = useState<boolean>(false);

    const loadData = async (id: number) => {
        try {
            setIsLoading(true);
            const [tt, dt] = await Promise.all([
                layThongTinThueTongHop(id),
                taiThueTongHop(id)
            ]);
            setThongTin(tt);
            setDataChiTiet(dt);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const idStr = queryParams.get('id');
        if (idStr) {
            const id = parseInt(idStr, 10);
            if (!isNaN(id) && id > 0) {
                setIdThueThang(id);
                loadData(id);
            }
        }
    }, [location.search]);

    const search = () => {
        setTuKhoaFilter(tuKhoa.trim().toLowerCase());
    };

    // Tải lại / Tính thuế
    const handleTinhThue = async () => {
        if (!idThueThang) return;
        try {
            setIsLoading(true);
            const dt = await taiThueTongHop(idThueThang);
            setDataChiTiet(dt);
            toast.success("Tính thuế thành công");
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    // Lưu
    const handleLuu = async () => {
        if (!idThueThang) return;
        try {
            setIsLoading(true);
            await luuThueTongHop({
                idThueThang: idThueThang,
                dsChiTietTNCN: dataChiTiet?.dsThueTNCN,
                dsIdThueTNCN: thongTin?.dsIdThueTNCN,
                dsIdThueKTX: thongTin?.dsIdThueKTX
            });
            toast.success("Lưu thuế thành công");
            loadData(idThueThang);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    // Xác nhận duyệt
    const handleDuyet = async () => {
        if (!idThueThang) return;
        try {
            setIsLoading(true);
            await duyetThueTongHop(idThueThang);
            toast.success("Xác nhận thuế thành công");
            setIsShowModalDuyet(false);
            loadData(idThueThang);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    // Xuất Excel
    const handleExport = async () => {
        if (!idThueThang) return;
        try {
            await xuatExcelThueTongHop(idThueThang, thongTin?.tenKyThue || `TongHopThue_${idThueThang}`);
            toast.success("Xuất file Excel thành công");
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        }
    };

    // Mở Modal Cấu hình tham chiếu
    const handleOpenModalCauHinh = async () => {
        if (!thongTin) return;
        const curYear = thongTin.nam || dayjs().year();
        setNamLocTNCN(dayjs().year(curYear));
        setNamLocKTX(dayjs().year(curYear));

        try {
            const [tncn, ktx] = await Promise.all([
                layDsThueThangTheoNam(curYear),
                layDanhSachKyThueKhongThuongXuyen(curYear.toString())
            ]);
            setDsKyThueTNCNModal(tncn || []);
            setDsKyThueKTXModal(ktx || []);
        } catch (e) { }

        formCauHinh.setFieldsValue({
            dsIdThueTNCN: thongTin.dsIdThueTNCN || [],
            dsIdThueKTX: thongTin.dsIdThueKTX || []
        });

        setOpenModalCauHinh(true);
    };

    const onFinishCauHinh = async (values: any) => {
        if (!idThueThang) return;
        try {
            setIsLoading(true);
            await capNhatThamChieuThueTongHop({
                idThueThang: idThueThang,
                dsIdThueTNCN: values.dsIdThueTNCN || [],
                dsIdThueKTX: values.dsIdThueKTX || []
            });
            toast.success("Cập nhật các kỳ tham chiếu thành công!");
            setOpenModalCauHinh(false);
            loadData(idThueThang);
        } catch (err: any) {
            toast.error(err?.toString().replace("Error: ", ""));
        } finally {
            setIsLoading(false);
        }
    };

    const optionsTNCN = dsKyThueTNCNModal.map(x => ({
        value: x.id,
        label: `${x.tenKyThue} (Số NV: ${x.soNhanVien || 0})`
    }));

    const optionsKTX = dsKyThueKTXModal.map(x => ({
        value: x.id,
        label: `${x.kyThue} (Số NSK: ${x.soNhanVien || 0})`
    }));

    const isDaDuyet = thongTin?.trangThai === trangThaiThue.duyet;

    // Filter lists by tuKhoaFilter
    const dsTNCNFiltered = (dataChiTiet?.dsThueTNCN || []).filter(item => {
        if (!tuKhoaFilter) return true;
        const name = (item.hoTen || '').toLowerCase();
        const code = (item.ma || '').toLowerCase();
        const chucVu = (item.tenChucVu || '').toLowerCase();
        const donVi = (item.tenDonVi || '').toLowerCase();
        return name.includes(tuKhoaFilter) || code.includes(tuKhoaFilter) || chucVu.includes(tuKhoaFilter) || donVi.includes(tuKhoaFilter);
    });

    const dsKTXFiltered = (dataChiTiet?.dsThueKTX || []).filter(item => {
        if (!tuKhoaFilter) return true;
        const name = (item.hoTen || '').toLowerCase();
        const code = (item.maNhanVien || '').toLowerCase();
        const chucVu = (item.tenChucVu || '').toLowerCase();
        const nhom = (item.tenNhomNhanSu || '').toLowerCase();
        return name.includes(tuKhoaFilter) || code.includes(tuKhoaFilter) || chucVu.includes(tuKhoaFilter) || nhom.includes(tuKhoaFilter);
    });

    const demSoKhoan = (ds?: number[]) => {
        if (!ds || ds.length === 0) return '0 kỳ';
        return `${ds.length} kỳ`;
    };

    // ==========================================
    // CỘT ĐỘNG PHẦN I: THUẾ TNCN THƯỜNG XUYÊN
    // ==========================================
    const dynamicHeadersTNCN = dataChiTiet?.dynamicColumnHeadersTNCN ?? [];
    const thuNhapColsTNCN = dynamicHeadersTNCN.filter((h: DynamicColumnHeader) => h.nhomCot === 0);
    const giamTruBHColsTNCN = dynamicHeadersTNCN.filter((h: DynamicColumnHeader) => h.nhomCot === 1);

    const columnsTNCN: any[] = [
        {
            title: 'STT',
            key: 'stt',
            width: 50,
            align: 'center' as const,
            fixed: 'left' as const,
            render: (_text: any, _record: any, index: number) => index + 1,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'hoTen',
            key: 'hoTen',
            width: 190,
            fixed: 'left' as const,
            render: (text: string) => <strong>{text}</strong>,
        },
        {
            title: 'Mã NV',
            dataIndex: 'ma',
            key: 'ma',
            width: 100,
            align: 'center' as const,
            render: (text: string) => text || '-',
        },
        {
            title: 'Chức vụ',
            dataIndex: 'tenChucVu',
            key: 'tenChucVu',
            width: 150,
        },
        {
            title: 'Đơn vị / Nhóm',
            key: 'donViNhom',
            width: 160,
            render: (_text: any, record: any) => record.tenDonVi || record.tenNhom || '-',
        },
        ...(thuNhapColsTNCN.length > 0 ? [{
            title: 'Thu nhập chịu thuế',
            children: thuNhapColsTNCN.map((h: DynamicColumnHeader) => ({
                title: h.name,
                key: `salary_${h.loai}_${h.id}`,
                width: 150,
                align: 'right' as const,
                render: (_: any, row: ChiTietThueThangItem) =>
                    formatVND(row.dynamicColumns?.[`salary_${h.loai}_${h.id}`] ?? 0),
            }))
        }] : []),
        {
            title: 'Tổng TN chịu thuế',
            dataIndex: 'thuNhapChiuThue',
            key: 'thuNhapChiuThue',
            width: 150,
            align: 'right' as const,
            render: (v: number) => <strong>{formatVND(v)}</strong>,
        },
        {
            title: 'Các khoản giảm trừ',
            children: [
                {
                    title: 'Bản thân',
                    key: 'giamTruBanThan',
                    dataIndex: 'giamTruBanThan',
                    width: 130,
                    align: 'right' as const,
                    render: (v: number) => formatVND(v),
                },
                {
                    title: 'Phụ thuộc',
                    key: 'giamTruNguoiPhuThuoc',
                    dataIndex: 'giamTruNguoiPhuThuoc',
                    width: 130,
                    align: 'right' as const,
                    render: (v: number) => formatVND(v),
                },
                {
                    title: 'Số NPT',
                    key: 'soNguoiPhuThuoc',
                    dataIndex: 'soNguoiPhuThuoc',
                    width: 90,
                    align: 'center' as const,
                    render: (v: number) => v,
                },
                ...(giamTruBHColsTNCN.length > 0 ? [{
                    title: 'Các khoản BH phải nộp',
                    children: giamTruBHColsTNCN.map((h: DynamicColumnHeader) => ({
                        title: h.name,
                        key: `salary_${h.loai}_${h.id}`,
                        width: 150,
                        align: 'right' as const,
                        render: (_: any, row: ChiTietThueThangItem) =>
                            formatVND(row.dynamicColumns?.[`salary_${h.loai}_${h.id}`] ?? 0),
                    })),
                }] : []),
                {
                    title: 'Tổng cộng',
                    dataIndex: 'tongCongGiamTru',
                    key: 'tongCongGiamTru',
                    width: 150,
                    align: 'right' as const,
                    render: (v: number) => formatVND(v),
                },
                {
                    title: 'TNCT trả cho cá nhân',
                    dataIndex: 'thuNhapTraChoCaNhan',
                    key: 'thuNhapTraChoCaNhan',
                    width: 160,
                    align: 'right' as const,
                    render: (v: number) => formatVND(v),
                },
            ],
        },
        {
            title: 'Thu nhập tính thuế',
            dataIndex: 'thuNhapTinhThue',
            key: 'thuNhapTinhThue',
            width: 150,
            align: 'right' as const,
            render: (v: number) => <strong>{formatVND(v)}</strong>,
        },
        {
            title: 'Thuế phải nộp',
            dataIndex: 'thuePhaiNop',
            key: 'thuePhaiNop',
            width: 140,
            align: 'right' as const,
            render: (v: number) => <span style={{ color: '#d9534f', fontWeight: 600 }}>{formatVND(v)}</span>,
        },
        {
            title: 'Thuế đã nộp',
            dataIndex: 'thueDaNop',
            key: 'thueDaNop',
            width: 130,
            align: 'right' as const,
            render: (v: number) => formatVND(v),
        },
        {
            title: 'Thuế còn phải nộp',
            dataIndex: 'thueConPhaiNop',
            key: 'thueConPhaiNop',
            width: 140,
            align: 'right' as const,
            render: (v: number) => <span style={{ color: '#c9302c', fontWeight: 700 }}>{formatVND(v)}</span>,
        },
    ];

    // ==========================================
    // CỘT ĐỘNG PHẦN II: THUẾ KTX
    // ==========================================
    const ktxColNames = Array.from(
        new Set(
            (dataChiTiet?.dsThueKTX || [])
                .flatMap(x => x.dynamicColumns || [])
                .map(x => x.tenThuNhap)
        )
    );

    const dynamicColumnsKTX: ColumnsType<any> = ktxColNames.map((colName) => ({
        title: colName,
        key: `dyn_${colName}`,
        width: 130,
        align: 'right',
        render: (_text, record) => {
            const col = record.dynamicColumns?.find((c: any) => c.tenThuNhap === colName);
            return formatVND(col ? col.mucThuNhap : 0);
        }
    }));

    const columnsKTX: ColumnsType<any> = [
        {
            title: 'STT',
            key: 'stt',
            width: 60,
            align: 'center',
            render: (_text, _record, index) => index + 1,
        },
        {
            title: 'Mã NV/NSK',
            dataIndex: 'maNhanVien',
            key: 'maNhanVien',
            width: 100,
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: 'Họ và tên',
            dataIndex: 'hoTen',
            key: 'hoTen',
            width: 180,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Chức vụ',
            dataIndex: 'tenChucVu',
            key: 'tenChucVu',
            width: 150,
        },
        {
            title: 'Nhóm nhân sự',
            dataIndex: 'tenNhomNhanSu',
            key: 'tenNhomNhanSu',
            width: 160,
            render: (text) => text || '-',
        },
        ...dynamicColumnsKTX,
        {
            title: 'Tổng TN chịu thuế',
            dataIndex: 'tongThuNhapChiuThue',
            key: 'tongThuNhapChiuThue',
            width: 140,
            align: 'right',
            render: (val) => <strong>{formatVND(val)}</strong>,
        },
        {
            title: 'Thuế TNCN 10%',
            dataIndex: 'thuePhaiNop',
            key: 'thuePhaiNop',
            width: 140,
            align: 'right',
            render: (val) => <span style={{ color: '#d9534f', fontWeight: 700 }}>{formatVND(val)}</span>,
        },
    ];

    return (
        <section>
            <ContentHeader title="Tổng hợp thuế" />

            <Row className="form-group" gutter={8}>
                <Col xs={22} sm={14} md={8}>
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Nhập tên, mã nhân viên"
                        value={tuKhoa}
                        onChange={(e) => setTuKhoa(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') search(); }}
                    />
                </Col>
                <Col xs={2} sm={2} md={0}>
                    <Button type="primary" icon={<SearchOutlined />} onClick={search} />
                </Col>
                <Col xs={0} sm={0} md={4}>
                    <Button type="primary" icon={<SearchOutlined />} onClick={search}>Tìm kiếm</Button>
                </Col>
                <Col xs={24} sm={24} md={12}>
                    <Space className="float-right">
                        <Button type="default" className="hrm-btn hrm-btn-warning" icon={<ArrowLeftOutlined />} onClick={() => navigate(Links.Thue.TongHopThue)}>
                            Quay lại
                        </Button>
                        {!isDaDuyet && (
                            <Button type="primary" icon={<DownloadOutlined />} onClick={handleTinhThue} loading={isLoading}>Tính thuế</Button>
                        )}
                        {!isDaDuyet && (
                            <Button type="primary" icon={<SendOutlined />} onClick={() => setIsShowModalDuyet(true)}>Xác nhận</Button>
                        )}
                        {!isDaDuyet && (
                            <Button className="hrm-btn hrm-btn-warning" icon={<SaveOutlined />} onClick={handleLuu} loading={isLoading}>Lưu</Button>
                        )}
                        <Button type="default" icon={<DownloadOutlined />} onClick={handleExport}>Xuất Excel</Button>
                    </Space>
                </Col>
            </Row>

            {/* Dữ liệu tham chiếu */}
            <Row gutter={8} className="form-group">
                <Col xs={24}>
                    <div className="card card-primary" style={{ padding: '15px' }}>
                        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>Dữ liệu tham chiếu</div>
                            {!isDaDuyet && (
                                <Button type="primary" size="small" icon={<EditOutlined />} onClick={handleOpenModalCauHinh}>
                                    Cập nhật
                                </Button>
                            )}
                        </div>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <div style={{ fontSize: '12px', color: '#666' }}>Kỳ Thuế TNCN (Thường xuyên)</div>
                                <div style={{ fontSize: '13px', marginTop: '5px' }}>{demSoKhoan(thongTin?.dsIdThueTNCN)}</div>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <div style={{ fontSize: '12px', color: '#666' }}>Kỳ Thuế KTX (Không thường xuyên)</div>
                                <div style={{ fontSize: '13px', marginTop: '5px' }}>{demSoKhoan(thongTin?.dsIdThueKTX)}</div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Dashboard Thống kê tổng hợp toàn bộ cơ quan (Đưa lên đầu trang) */}
            {dataChiTiet && (
                <Row gutter={[12, 12]} className="mb-3">
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng người nộp thuế"
                                value={dataChiTiet.dsThueTNCN.length + dataChiTiet.dsThueKTX.length}
                                valueStyle={{ color: '#0c4da2', fontSize: 18, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng TN chịu thuế toàn CQ"
                                value={formatVND(dataChiTiet.tongThuNhapChiuThueToanBo)}
                                valueStyle={{ color: '#0c4da2', fontSize: 16, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng giảm trừ TNCN"
                                value={formatVND(dataChiTiet.dsThueTNCN.reduce((s, x) => s + (x.tongCongGiamTru || 0), 0))}
                                valueStyle={{ color: '#389e0d', fontSize: 16, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng thuế TNCN toàn CQ"
                                value={formatVND(dataChiTiet.tongThueTNCNToanBo)}
                                valueStyle={{ color: '#cf1322', fontSize: 16, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng thuế TNCN đã nộp"
                                value={formatVND(dataChiTiet.tongThueDaNopTNCN)}
                                valueStyle={{ color: '#389e0d', fontSize: 16, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Card bordered={false} className="bg-light" style={{ height: '100%' }}>
                            <Statistic
                                title="Tổng thuế còn phải nộp"
                                value={formatVND(dataChiTiet.tongThueConPhaiNopTNCN + dataChiTiet.tongThueTNCNKTX)}
                                valueStyle={{ color: '#d4380d', fontSize: 16, fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Phần I: Thuế TNCN Thường xuyên (Nằm ở trên) */}
            <Card
                className="mb-4"
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>Phần I</Tag>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#0c4da2' }}>
                            THUẾ THU NHẬP CÁ NHÂN (THƯỜNG XUYÊN)
                        </span>
                        <Tag color="default">Số nhân sự: {dsTNCNFiltered.length}</Tag>
                    </div>
                }
            >
                <Table
                    columns={columnsTNCN}
                    dataSource={dsTNCNFiltered.map((item, idx) => ({ ...item, key: `tncn_${item.id || idx}` }))}
                    pagination={false}
                    scroll={{ x: 1800 }}
                    bordered
                    locale={{ emptyText: "Chưa có dữ liệu thuế TNCN thường xuyên" }}
                    summary={() => {
                        if (dsTNCNFiltered.length === 0) return null;
                        const sumTN = dsTNCNFiltered.reduce((s, x) => s + (x.thuNhapChiuThue || 0), 0);
                        const sumGTBT = dsTNCNFiltered.reduce((s, x) => s + (x.giamTruBanThan || 0), 0);
                        const sumGTNPT = dsTNCNFiltered.reduce((s, x) => s + (x.giamTruNguoiPhuThuoc || 0), 0);
                        const sumTongGT = dsTNCNFiltered.reduce((s, x) => s + (x.tongCongGiamTru || 0), 0);
                        const sumTraCN = dsTNCNFiltered.reduce((s, x) => s + (x.thuNhapTraChoCaNhan || 0), 0);
                        const sumTNTT = dsTNCNFiltered.reduce((s, x) => s + (x.thuNhapTinhThue || 0), 0);
                        const sumThuePhaiNop = dsTNCNFiltered.reduce((s, x) => s + (x.thuePhaiNop || 0), 0);
                        const sumThueDaNop = dsTNCNFiltered.reduce((s, x) => s + (x.thueDaNop || 0), 0);
                        const sumThueConPhaiNop = dsTNCNFiltered.reduce((s, x) => s + (x.thueConPhaiNop || 0), 0);

                        let colIndex = 0;
                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={colIndex++} colSpan={5} align="center">
                                    Tổng cộng Phần I
                                </Table.Summary.Cell>
                                {thuNhapColsTNCN.map((h: DynamicColumnHeader, idx: number) => {
                                    const key = `salary_${h.loai}_${h.id}`;
                                    const sumVal = dsTNCNFiltered.reduce((s, x) => s + (x.dynamicColumns?.[key] ?? 0), 0);
                                    return (
                                        <Table.Summary.Cell key={`sum_tn_${idx}`} index={colIndex++} align="right">
                                            {formatVND(sumVal)}
                                        </Table.Summary.Cell>
                                    );
                                })}
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumTN)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumGTBT)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumGTNPT)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="center">
                                    -
                                </Table.Summary.Cell>
                                {giamTruBHColsTNCN.map((h: DynamicColumnHeader, idx: number) => {
                                    const key = `salary_${h.loai}_${h.id}`;
                                    const sumVal = dsTNCNFiltered.reduce((s, x) => s + (x.dynamicColumns?.[key] ?? 0), 0);
                                    return (
                                        <Table.Summary.Cell key={`sum_bh_${idx}`} index={colIndex++} align="right">
                                            {formatVND(sumVal)}
                                        </Table.Summary.Cell>
                                    );
                                })}
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumTongGT)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumTraCN)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumTNTT)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right" style={{ color: '#d9534f' }}>
                                    {formatVND(sumThuePhaiNop)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right">
                                    {formatVND(sumThueDaNop)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={colIndex++} align="right" style={{ color: '#c9302c' }}>
                                    {formatVND(sumThueConPhaiNop)}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}
                />
            </Card>

            {/* Phần II: Thuế TNCN Không thường xuyên (KTX) (Nằm ở dưới) */}
            <Card
                className="mb-4"
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color="orange" style={{ fontSize: 13, padding: '2px 8px' }}>Phần II</Tag>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#e67e22' }}>
                            THUẾ THU NHẬP CÁ NHÂN (KHÔNG THƯỜNG XUYÊN - KTX)
                        </span>
                        <Tag color="default">Số nhân sự: {dsKTXFiltered.length}</Tag>
                    </div>
                }
            >
                <Table
                    columns={columnsKTX}
                    dataSource={dsKTXFiltered.map((item, idx) => ({ ...item, key: `ktx_${item.idNhanVien || idx}` }))}
                    pagination={false}
                    scroll={{ x: 1300 }}
                    bordered
                    locale={{ emptyText: "Chưa có dữ liệu thuế không thường xuyên (KTX)" }}
                    summary={() => {
                        if (dsKTXFiltered.length === 0) return null;
                        const sumTNKTX = dsKTXFiltered.reduce((s, x) => s + (x.tongThuNhapChiuThue || 0), 0);
                        const sumThueKTX = dsKTXFiltered.reduce((s, x) => s + (x.thuePhaiNop || 0), 0);

                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={5} align="center">
                                    Tổng cộng Phần II
                                </Table.Summary.Cell>
                                {ktxColNames.map((colName, cIdx) => {
                                    const sumCol = dsKTXFiltered
                                        .flatMap(x => x.dynamicColumns || [])
                                        .filter(x => x.tenThuNhap === colName)
                                        .reduce((acc, cur) => acc + (cur.mucThuNhap || 0), 0);
                                    return (
                                        <Table.Summary.Cell key={cIdx} index={5 + cIdx} align="right">
                                            {formatVND(sumCol)}
                                        </Table.Summary.Cell>
                                    );
                                })}
                                <Table.Summary.Cell index={5 + ktxColNames.length} align="right">
                                    {formatVND(sumTNKTX)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6 + ktxColNames.length} align="right" style={{ color: '#d9534f' }}>
                                    {formatVND(sumThueKTX)}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        );
                    }}
                />
            </Card>

            {/* Modal Cấu hình tham chiếu */}
            {openModalCauHinh && (
                <ModalTextForm
                    title="Cấu hình các kỳ thuế tham chiếu"
                    show={openModalCauHinh}
                    hideFooter={true}
                    handleClose={() => setOpenModalCauHinh(false)}
                    size="lg"
                >
                    <Form
                        form={formCauHinh}
                        layout="vertical"
                        onFinish={onFinishCauHinh}
                    >
                        <div style={{ marginBottom: 12 }}>
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item label="Năm Thuế TNCN" style={{ marginBottom: 0 }}>
                                        <DatePicker
                                            picker="year"
                                            style={{ width: "100%" }}
                                            value={namLocTNCN}
                                            onChange={async (d) => {
                                                setNamLocTNCN(d);
                                                try {
                                                    const res = await layDsThueThangTheoNam(d ? d.year() : undefined);
                                                    setDsKyThueTNCNModal(res || []);
                                                } catch (e) { }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Kỳ Thuế TNCN (Thường xuyên)" name="dsIdThueTNCN" style={{ marginBottom: 0 }}>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Chọn các kỳ thuế TNCN..."
                                            options={optionsTNCN}
                                            notFoundContent="Không có dữ liệu"
                                            filterOption={(input, option: any) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <Row gutter={8}>
                                <Col span={8}>
                                    <Form.Item label="Năm Thuế KTX" style={{ marginBottom: 0 }}>
                                        <DatePicker
                                            picker="year"
                                            style={{ width: "100%" }}
                                            value={namLocKTX}
                                            onChange={async (d) => {
                                                setNamLocKTX(d);
                                                try {
                                                    const res = await layDanhSachKyThueKhongThuongXuyen(d ? d.year().toString() : "");
                                                    setDsKyThueKTXModal(res || []);
                                                } catch (e) { }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={16}>
                                    <Form.Item label="Kỳ Thuế KTX (Không thường xuyên)" name="dsIdThueKTX" style={{ marginBottom: 0 }}>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            placeholder="Chọn các kỳ thuế KTX..."
                                            options={optionsKTX}
                                            notFoundContent="Không có dữ liệu"
                                            filterOption={(input, option: any) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        <Row justify="end" className="mt-3">
                            <Space>
                                <Button onClick={() => setOpenModalCauHinh(false)}>Hủy</Button>
                                <Button type="primary" htmlType="submit" loading={isLoading}>
                                    Lưu & Cập nhật
                                </Button>
                            </Space>
                        </Row>
                    </Form>
                </ModalTextForm>
            )}

            {/* Modal Xác nhận duyệt */}
            <Modal
                title="Xác nhận duyệt"
                open={isShowModalDuyet}
                onOk={handleDuyet}
                onCancel={() => setIsShowModalDuyet(false)}
                okText="Xác nhận"
                cancelText="Hủy"
            >
                <p>Bạn có chắc chắn muốn xác nhận duyệt kỳ tổng hợp thuế này?</p>
            </Modal>
        </section>
    );
};

export default ChiTietTongHopThue;
